<template>
  <div>
    <div style="padding-top: 55px" />
    <div
      class="album py-4 bg-light"
      style="overflow: auto; height: calc(100vh - 55px)"
    >
      <div class="container">
        <h2 class="text-center mb-4">System Monitor</h2>

        <!-- Software Info -->
        <div class="card mb-4" v-if="systemStatus.software">
          <div class="card-header bg-primary text-white">
            <strong>Software Information</strong>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-4">
                <strong>Name:</strong> {{ systemStatus.software.name }}
              </div>
              <div class="col-md-4">
                <strong>인허가 버전:</strong> {{ systemStatus.software.version }}
              </div>
              <div class="col-md-4">
                <strong>Serial:</strong> {{ systemStatus.software.serial_number }}
              </div>
            </div>
          </div>
        </div>

        <!-- Console Links -->
        <div class="card mb-4" v-if="systemStatus.console_links">
          <div class="card-header bg-info text-white">
            <strong>Management Consoles</strong>
          </div>
          <div class="card-body">
            <div class="row">
              <div
                class="col-md-4 mb-2"
                v-for="(link, key) in systemStatus.console_links"
                :key="key"
              >
                <a
                  :href="getConsoleUrl(link)"
                  target="_blank"
                  class="btn btn-outline-primary btn-block"
                >
                  <i class="fa fa-external-link mr-2"></i>
                  {{ link.name }}
                  <span class="badge badge-secondary ml-1">:{{ link.port }}</span>
                </a>
                <small class="text-muted d-block mt-1">{{ link.description }}</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Component Status -->
        <div class="card mb-4">
          <div class="card-header bg-dark text-white d-flex justify-content-between align-items-center">
            <strong>Component Status & Configuration</strong>
            <button class="btn btn-sm btn-light" @click="fetchSystemStatus">
              Refresh
            </button>
          </div>
          <div class="card-body p-0">
            <div class="accordion" id="componentsAccordion">
              <div
                class="card mb-0 border-left-0 border-right-0"
                v-for="(component, key) in systemStatus.components"
                :key="key"
              >
                <div
                  class="card-header d-flex justify-content-between align-items-center"
                  :id="'heading-' + key"
                  style="cursor: pointer"
                  @click="toggleComponent(key)"
                >
                  <div>
                    <span
                      class="badge mr-2"
                      :class="getStatusBadgeClass(component.status)"
                    >
                      {{ component.status }}
                    </span>
                    <strong>{{ component.name }}</strong>
                    <span v-if="component.queue_length !== undefined" class="ml-2 text-muted">
                      (Queue: {{ component.queue_length }})
                    </span>
                    <span v-if="component.object_count !== undefined" class="ml-2 text-muted">
                      (Objects: {{ component.object_count }})
                    </span>
                    <span v-if="component.total_pool_size !== undefined" class="ml-2 text-muted">
                      (Pool: {{ component.total_pool_size }}, Tasks: {{ component.active_tasks || 0 }})
                    </span>
                    <span v-if="component.consumer_count !== undefined" class="ml-2 text-muted">
                      (Consumers: {{ component.consumer_count }})
                    </span>
                  </div>
                  <i :class="expandedComponents[key] ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                </div>
                <div v-show="expandedComponents[key]" class="card-body bg-light">
                  <table class="table table-sm table-bordered mb-0">
                    <tbody>
                      <tr v-for="(value, configKey) in component.config" :key="configKey">
                        <td class="font-weight-bold" style="width: 40%">{{ configKey }}</td>
                        <td>
                          <code>{{ value || '(not set)' }}</code>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <!-- Additional info for workers -->
                  <div v-if="component.worker_names" class="mt-2">
                    <strong>Active Workers:</strong>
                    <span v-for="(name, idx) in component.worker_names" :key="idx" class="badge badge-info ml-1">
                      {{ name }}
                    </span>
                  </div>
                  <div v-if="component.registered_tasks" class="mt-1">
                    <small class="text-muted">Registered Tasks: {{ component.registered_tasks }}</small>
                  </div>
                  <!-- Note -->
                  <div v-if="component.note" class="alert alert-info mt-2 mb-0">
                    <small><i class="fa fa-info-circle mr-1"></i>{{ component.note }}</small>
                  </div>
                  <!-- Error -->
                  <div v-if="component.error" class="alert alert-danger mt-2 mb-0">
                    <strong>Error:</strong> {{ component.error }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Dataset Settings (PACS/EMR) -->
        <div class="card mb-4">
          <div class="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
            <strong>Dataset Settings (PACS/EMR)</strong>
            <button class="btn btn-sm btn-light" @click="fetchDatasetSettings">
              Refresh
            </button>
          </div>
          <div class="card-body p-0">
            <table class="table table-hover table-sm mb-0">
              <thead class="thead-light">
                <tr>
                  <th>Dataset Name</th>
                  <th>Owner</th>
                  <th>PACS Query</th>
                  <th>PACS Upload</th>
                  <th>EMR Query</th>
                  <th>EMR Send</th>
                  <th>Settings</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dataset in datasetSettings" :key="dataset.id">
                  <td>{{ dataset.name }}</td>
                  <td>
                    <span class="badge badge-primary">{{ dataset.owner }}</span>
                  </td>
                  <td>
                    <span v-if="dataset.pacs_settings.REQUIRE === 'true'" class="text-success">
                      <i class="fa fa-check-circle"></i>
                      <small>{{ dataset.pacs_settings.AETITLE }}</small>
                    </span>
                    <span v-else class="text-muted">
                      <i class="fa fa-times-circle"></i>
                    </span>
                  </td>
                  <td>
                    <span v-if="dataset.pacs_settings.REQUIRESEND === 'true'" class="text-success">
                      <i class="fa fa-check-circle"></i>
                      <span class="badge badge-info ml-1">PDF</span>
                    </span>
                    <span v-else class="text-muted">
                      <i class="fa fa-times-circle"></i>
                    </span>
                  </td>
                  <td>
                    <span v-if="dataset.emr_settings.REQUIRE === 'true'" class="text-success">
                      <i class="fa fa-check-circle"></i>
                      <small>{{ dataset.emr_settings.TYPE || 'mics' }}</small>
                    </span>
                    <span v-else class="text-muted">
                      <i class="fa fa-times-circle"></i>
                    </span>
                  </td>
                  <td>
                    <span v-if="dataset.emr_settings.REQUIRESEND === 'true'" class="text-success">
                      <i class="fa fa-check-circle"></i>
                    </span>
                    <span v-else class="text-muted">
                      <i class="fa fa-times-circle"></i>
                    </span>
                  </td>
                  <td>
                    <button
                      class="btn btn-sm btn-outline-primary"
                      @click="showDatasetDetail(dataset)"
                    >
                      <i class="fa fa-cog"></i> Details
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Dataset Detail Modal -->
        <b-modal
          id="datasetDetailModal"
          size="lg"
          ok-only
          ok-title="Close"
        >
          <template #modal-title v-if="selectedDataset">
            <i class="fa fa-database mr-2"></i>
            {{ selectedDataset.name }}
          </template>
          <div v-if="selectedDataset">
            <!-- All Settings as Table -->
            <table class="table table-sm table-bordered">
              <tbody>
                <!-- Dataset Info -->
                <tr class="thead-light">
                  <th colspan="2">Dataset Information</th>
                </tr>
                <tr>
                  <td style="width: 40%"><strong>Owner</strong></td>
                  <td><span class="badge badge-primary">{{ selectedDataset.owner }}</span></td>
                </tr>
                <tr>
                  <td><strong>Users</strong></td>
                  <td>
                    <span v-if="selectedDataset.users && selectedDataset.users.length > 0">
                      <span
                        v-for="(user, idx) in selectedDataset.users"
                        :key="idx"
                        class="badge badge-secondary mr-1"
                      >{{ user }}</span>
                    </span>
                    <span v-else class="text-muted">No users assigned</span>
                  </td>
                </tr>

                <!-- PACS Settings -->
                <tr class="thead-light">
                  <th colspan="2">PACS Settings</th>
                </tr>
                <tr>
                  <td style="width: 40%"><strong>REQUIRE PACS QUERY</strong></td>
                  <td>
                    <span :class="selectedDataset.pacs_settings.REQUIRE === 'true' ? 'text-success' : 'text-muted'">
                      <i :class="selectedDataset.pacs_settings.REQUIRE === 'true' ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                      {{ selectedDataset.pacs_settings.REQUIRE === 'true' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td><strong>AETITLE</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.AETITLE || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>IP</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.IP || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>PORT</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.PORT || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>MODALITY</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.MODALITY || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>FORCELOSSLESSTRANFERPROTOCOL</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.FORCELOSSLESSTRANFERPROTOCOL || 'false' }}</code></td>
                </tr>
                <tr>
                  <td><strong>REQUIRE PACS SEND (PDF)</strong></td>
                  <td>
                    <span :class="selectedDataset.pacs_settings.REQUIRESEND === 'true' ? 'text-success' : 'text-muted'">
                      <i :class="selectedDataset.pacs_settings.REQUIRESEND === 'true' ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                      {{ selectedDataset.pacs_settings.REQUIRESEND === 'true' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td><strong>PROCEDURE CODE</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.PROCEDURE_CODE || 'FTET0012H' }}</code></td>
                </tr>
                <tr>
                  <td><strong>PROCEDURE DESCRIPTION</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.PROCEDURE_DESCRIPTION || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>STUDY DESCRIPTION</strong></td>
                  <td><code>{{ selectedDataset.pacs_settings.STUDY_DESCRIPTION || '(not set)' }}</code></td>
                </tr>

                <!-- EMR Settings -->
                <tr class="thead-light">
                  <th colspan="2">EMR Settings</th>
                </tr>
                <tr>
                  <td><strong>REQUIRE EMR QUERY</strong></td>
                  <td>
                    <span :class="selectedDataset.emr_settings.REQUIRE === 'true' ? 'text-success' : 'text-muted'">
                      <i :class="selectedDataset.emr_settings.REQUIRE === 'true' ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                      {{ selectedDataset.emr_settings.REQUIRE === 'true' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td><strong>REQUIRE EMR SEND</strong></td>
                  <td>
                    <span :class="selectedDataset.emr_settings.REQUIRESEND === 'true' ? 'text-success' : 'text-muted'">
                      <i :class="selectedDataset.emr_settings.REQUIRESEND === 'true' ? 'fa fa-check-circle' : 'fa fa-times-circle'"></i>
                      {{ selectedDataset.emr_settings.REQUIRESEND === 'true' ? 'Yes' : 'No' }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td><strong>TYPE</strong></td>
                  <td><code>{{ selectedDataset.emr_settings.TYPE || 'mics' }}</code></td>
                </tr>
                <tr>
                  <td><strong>IP</strong></td>
                  <td><code>{{ selectedDataset.emr_settings.IP || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>PORT</strong></td>
                  <td><code>{{ selectedDataset.emr_settings.PORT || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>SERVICENAME</strong></td>
                  <td><code>{{ selectedDataset.emr_settings.SERVICENAME || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>USERNAME</strong></td>
                  <td><code>{{ selectedDataset.emr_settings.USERNAME || '(not set)' }}</code></td>
                </tr>
                <tr>
                  <td><strong>PASSWORD</strong></td>
                  <td><code>{{ selectedDataset.emr_settings.PASSWORD || '(not set)' }}</code></td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-modal>

        <!-- Loading Overlay -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
          <p class="mt-2">Loading system status...</p>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="alert alert-danger">
          <strong>Error:</strong> {{ error }}
        </div>

        <!-- Not On-Premise Warning -->
        <div v-if="!isOnpremise && !loading" class="alert alert-warning text-center">
          <i class="fa fa-exclamation-triangle mr-2"></i>
          This page is only available in on-premise deployment mode.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "SystemMonitor",
  data() {
    return {
      loading: true,
      error: null,
      isOnpremise: false,
      systemStatus: {
        software: null,
        components: {},
        console_links: {}
      },
      datasetSettings: [],
      selectedDataset: null,
      expandedComponents: {}
    };
  },
  mounted() {
    this.fetchSystemStatus();
    this.fetchDatasetSettings();
  },
  methods: {
    async fetchSystemStatus() {
      this.loading = true;
      this.error = null;
      try {
        const response = await axios.get("/api/system/status");
        this.systemStatus = response.data;
        this.isOnpremise = response.data.is_onpremise;

        // Expand all components by default
        Object.keys(response.data.components || {}).forEach(key => {
          this.$set(this.expandedComponents, key, true);
        });
      } catch (err) {
        if (err.response && err.response.status === 400) {
          this.isOnpremise = false;
        } else {
          this.error = (err.response && err.response.data && err.response.data.error) || err.message;
        }
      } finally {
        this.loading = false;
      }
    },
    async fetchDatasetSettings() {
      try {
        const response = await axios.get("/api/system/datasets/settings");
        this.datasetSettings = response.data.datasets;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch dataset settings:", err);
      }
    },
    toggleComponent(key) {
      this.$set(this.expandedComponents, key, !this.expandedComponents[key]);
    },
    getStatusBadgeClass(status) {
      switch (status) {
        case "healthy":
        case "running":
          return "badge-success";
        case "unhealthy":
          return "badge-danger";
        default:
          return "badge-secondary";
      }
    },
    getConsoleUrl(link) {
      // Build URL using current hostname with the specified port
      var hostname = window.location.hostname;
      return link.protocol + "://" + hostname + ":" + link.port;
    },
    showDatasetDetail(dataset) {
      // Deep copy to avoid modifying original
      this.selectedDataset = JSON.parse(JSON.stringify(dataset));
      this.$bvModal.show("datasetDetailModal");
    }
  }
};
</script>

<style scoped>
.card-header {
  font-size: 0.95rem;
}

.table th,
.table td {
  vertical-align: middle;
}

code {
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 0.85rem;
}

.badge {
  font-size: 0.75rem;
}

.custom-switch .custom-control-label::before {
  cursor: pointer;
}
</style>
